import axios from 'axios';
import { toast } from 'react-toastify';
import {
  clearSessionStorage,
  getTokenFromSessionStorage,
  getUserNameFromSessionStorage
} from '../services/session-storage-service';
import globalRouter from './globalRouter';
import { TOAST_MESSAGES, VALIDATION_APIS } from '../constants/global-constants';
import authenticateService from '../services/authenticateService';

const axiosInstance = axios.create({});
const controller = new AbortController();
const { signal } = controller;
axiosInstance.interceptors.request.use((request) => {
  request.headers.user_name = getUserNameFromSessionStorage();
  request.headers.Authorization = `Bearer ${getTokenFromSessionStorage()}`;
  return { ...request, signal };
});

let isRefreshing = false;
let failedQueue: any = [];

// eslint-disable-next-line jsdoc/require-jsdoc
const processQueue = (error: any, token: string | null = null): void => {
  failedQueue.forEach((prom: any) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const method = error.config.method.toUpperCase();
    const { url } = error.config;
    const isIncludedInValidation = VALIDATION_APIS.filter((item) => url.includes(item));
    if (error.response) {
      if (
        (error.response.status === 401 || error.response.status === 403) &&
        // eslint-disable-next-line no-underscore-dangle
        !originalRequest._retry
      ) {
        if (isRefreshing) {
          // eslint-disable-next-line func-names
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }
        // eslint-disable-next-line no-underscore-dangle
        originalRequest._retry = true;
        isRefreshing = true;

        const authResponse = await authenticateService.refreshToken();
        const { error: authError, data } = authResponse;
        if (!authError) {
          axiosInstance.defaults.headers.Authorization = `Bearer ${data?.accessToken}`;
          originalRequest.headers.Authorization = `Bearer ${data?.accessToken}`;
          processQueue(null, data?.accessToken);
          axiosInstance(originalRequest);
        } else {
          processQueue(authError, null);
          clearSessionStorage();
          if (globalRouter.navigate) {
            globalRouter.navigate('/auth/login?session=expired');
            toast.error('Session refresh failed. Please login again.');
          }
        }
      }
      if (
        error.response.status === 422 &&
        error.response?.data &&
        error.response?.data?.errors?.length > 0
      ) {
        toast.error(error.response?.data?.errors[0]);
      }
      if (
        method !== 'GET' &&
        isIncludedInValidation.length > 0 &&
        (error.response.status === 400 ||
          error.response.status === 500 ||
          error.response.status === 404) &&
        error.response?.data
      ) {
        toast.error(error.response?.data?.message || TOAST_MESSAGES.error);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
